/* eslint-disable radix */

import SuggestionsDropdown from '@finn-no/suggestions-dropdown';
import el from '@finn-no/create-element';
import tracking from '@finn-no/analytics';

export default function search(
    form,
    streamingSuggestUrl,
    suggestUrl,
    client,
) {
    const isGlobal = form.hasAttribute('data-global');
    const isDropdown = form.hasAttribute('data-is-dropdown');
    const where = form.getAttribute('data-where');
    const beta = form.getAttribute('data-beta');
    const searchKeys = form.getAttribute('data-search-keys') || '';
    const spinnerEle = form.querySelector('[data-spinner]');
    const inputEle = form.elements.q;
    const noHitsEle = el('div.frontpage-search__result', null, 'Ingen treff');
    const bapOpenFiltersOverlayEnabled = form.hasAttribute(
        'data-bap-open-filters-overlay-enabled',
    );

    let req;
    let canceler; // XHR abort
    let dropdownEle;
    let lastTerm = '';
    const items = [];
    let suggestionsDropdown;

    function getTrackingLocation(whereInput, searchKeysInput) {
        if (searchKeysInput === 'SEARCH_ID_JOB_FULLTIME') {
            return 'location:jobfront';
        }
        if (searchKeysInput === 'SEARCH_ID_BAP_ALL') {
            return 'location:bapfront';
        }
        return `location:${whereInput.toLowerCase()}`;
    }

    function trackSubmit() {
        tracking.trackEventLater({
            analyticsName: 'search.search-for-something.executed',
            eventFlags: 'success',
            stringValue: `${getTrackingLocation(
                where,
                searchKeys,
            )},type:freetext`,
        });
    }

    function trackPickedItem(dropdownEleInput, ele) {
        const index = Array.from(
            dropdownEleInput.querySelectorAll('[data-track]'),
        ).indexOf(ele);
        const data = JSON.parse(ele.getAttribute('data-track'));
        const groupIndex = JSON.parse(ele.getAttribute('data-group-index'));
        data['searchfront.index'] = index + 1;
        data['searchfront.groupindex'] = parseInt(groupIndex) + 1;
        tracking.trackEventLater({
            analyticsName: 'search.search-for-something.dropdown-item-picked',
            eventFlags: 'success',
            numberValue: index + 1,
            stringValue: `${getTrackingLocation(where, searchKeys)},type:${
                data['searchfront.type']
            }`,
        });
        tracking.trackEventLater({
            ecType: 'SEARCH',
            ecCategory: 'searchfront.click',
            eventData: data,
        });
    }

    function trackViewedItems(ele) {
        const itemEles = ele.querySelectorAll('[data-track]');
        Array.from(itemEles).forEach((itemEle, i) => {
            const data = JSON.parse(itemEle.getAttribute('data-track'));
            data['searchfront.index'] = i + 1;
            const groupIndex = itemEle.getAttribute('data-group-index');
            data['searchfront.groupindex'] = parseInt(groupIndex) + 1;
            tracking.trackEventLater({
                ecType: 'SEARCH',
                ecCategory: 'searchfront.view',
                eventData: data,
            });
        });
    }

    function escapeRegExp(s) {
        // https://github.com/benjamingr/RegExp.escape/blob/master/polyfill.js
        return String(s).replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
    }

    function renderHighlightedTerm(text) {
        // Turn "Term Term1 " into /(^|\s)(Term1|Term)/, the terms being escaped.
        // `(^|\s)` is added to only highlight beginning of words.
        const pattern = `(^|\\s)(${lastTerm
            .trim()
            .split(/\s+/)
            // Sort by length (longest first) since the matching is eager, e.g.:
            //  Bad:  "t t1".match(/t|t1/gi) -> ["t", "t"]
            //  Good: "t t1".match(/t1|t/gi) -> ["t", "t1"]
            .sort((term1, term2) => term2.length - term1.length)
            .map(escapeRegExp)
            .join('|')})`;
        const termsRegExp = new RegExp(pattern, 'gi');
        let termsToHighlight = text.match(termsRegExp);
        if (!termsToHighlight) {
            return [text];
        }

        termsToHighlight = termsToHighlight.map((s) => s.trim()); // some matches might include whitespace

        return text
            .split(termsRegExp)
            .filter(Boolean)
            .reduce(
                (prev, curr) =>
                    prev.concat(
                        termsToHighlight.indexOf(curr) === -1
                            ? curr
                            : el('b', null, curr),
                    ),
                [],
            );
    }

    function renderMainTitle(text, secondaryTitle) {
        const mainTitle = (secondaryTitle ? `${secondaryTitle} – ` : '') + text;
        return el('span', null, mainTitle);
    }

    function renderTitle(titleText, prefix, secondaryTitle) {
        const text = titleText || '';
        if (prefix) {
            return el(
                'span',
                { title: prefix + text, 'data-title': '' },
                renderHighlightedTerm(prefix),
                text ? renderMainTitle(text, secondaryTitle) : null,
            );
        }
        return el(
            'span',
            {
                title:
                    (secondaryTitle
                        ? secondaryTitle + (text ? ' – ' : '')
                        : '') + text,
                'data-title': '',
            },
            secondaryTitle ? secondaryTitle + (text ? ' – ' : '') : null,
            text,
        );
    }

    function renderInfoSingleLine(text) {
        return el(
            'span.frontpage-search__result__minor-text.frontpage-search--long-text',
            { title: text },
            renderHighlightedTerm(text),
        );
    }

    function renderInfo2(text) {
        return el('span.frontpage-search__result__minor-text', null, text);
    }

    function renderResultCompanyProfile(result, index) {
        return el(
            'li.frontpage-search__result__item',
            null,
            el(
                'a.frontpage-search__result__link',
                {
                    href: result.url,
                    'data-group-index': index,
                    'data-track': JSON.stringify(result.data.track),
                },
                el('img.frontpage-search__result__image', {
                    src: result.imgUrl.replace('640w', '240w'),
                    style: 'max-height: 100%; padding: 5px;',
                }),
                el(
                    'div.frontpage-search__result__text',
                    null,
                    renderTitle(result.title, result.titleSupplement),
                    result.info1 ? renderInfoSingleLine(result.info1) : null,
                    result.info2 ? renderInfo2(result.info2) : null,
                ),
            ),
        );
    }

    function renderGroupCompanyProfile(group) {
        const groupingHeader = group.groupingHeader
            ? group.groupingHeader
            : 'Bedriftsprofiler';
        return el(
            'div.frontpage-search__result',
            { 'data-group-type': group.type },
            el(
                'h2.frontpage-search__result__group-title',
                null,
                groupingHeader,
            ),
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation', style: 'width: 100%' },
                group.results.map(renderResultCompanyProfile),
            ),
        );
    }

    function renderResultSearchFinn(result, index) {
        const url = new URL(result.url);
        return el(
            'li.frontpage-search__result__item',
            null,
            el(
                'a.frontpage-search__result__link',
                {
                    href: url.href,
                    'data-group-index': index,
                    'data-choose-me': result.chooseMe,
                    'data-track': JSON.stringify(result.data.track),
                },
                el(
                    'svg:svg.frontpage-search__result__image',
                    { width: '34', height: '34', viewBox: '0 0 34 34' },
                    el('svg:path', {
                        d:
                            'M29.555 29.586l.03-.03-6.007-6.008.596-.703a11.393 11.393 0 0 0 2.715-7.4C26.889 9.123 ' +
                            '21.765 4 15.444 4 9.124 4 4 9.124 4 15.444 4 21.765 9.124 26.89 15.444 26.89c2.75 ' +
                            '0 5.348-.972 7.401-2.715l.703-.596 6.007 6.008z',
                        fill: 'none',
                        stroke: '#04BEFB',
                        'stroke-width': '2',
                    }),
                ),
                ' ',
                el(
                    'div.frontpage-search__result__text',
                    { 'data-title': '' },
                    'Finn flere resultater for ',
                    el('b', null, result.titleSupplement.substring(26)),
                ),
            ),
        );
    }

    function renderGroupSearchFinn(group) {
        return el(
            'div.frontpage-search__result',
            { 'data-group-type': group.type },
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation' },
                group.results.map(renderResultSearchFinn),
            ),
        );
    }

    function renderResultAd(result, index) {
        return el(
            'li.frontpage-search__result__item',
            null,
            el(
                'a.frontpage-search__result__link',
                {
                    href: result.url,
                    'data-automation-id': 'global_search_suggestion',
                    'data-group-index': index,
                    'data-choose-me': result.chooseMe,
                    'data-track': JSON.stringify(result.data.track),
                },
                el('img.frontpage-search__result__image', {
                    src: result.imgUrl.replace(
                        /\/mmo\/|\/dynamic\/default\//,
                        '/dynamic/300x225c/',
                    ),
                    width: '100',
                    height: '75',
                }),
                el(
                    'div.frontpage-search__result__text',
                    null,
                    renderTitle(result.title, result.titleSupplement),
                    renderInfoSingleLine(result.info1),
                ),
            ),
        );
    }

    function renderGroupAd(group) {
        return el(
            'div.frontpage-search__result',
            { 'data-group-type': group.type },
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation' },
                group.results.map(renderResultAd),
            ),
        );
    }

    function renderResult(result, index) {
        const title = [];
        if (result.titleSupplement) {
            if (result.title) {
                if (result.secondaryTitle) {
                    title.push(result.secondaryTitle, '–');
                }
                title.push(result.titleSupplement, result.title);
            } else {
                title.push(result.titleSupplement);
                if (result.secondaryTitle) {
                    title.push(result.secondaryTitle);
                }
            }
        } else if (result.title) {
            if (result.secondaryTitle) {
                title.push(result.secondaryTitle, '–');
            }
            title.push(result.title);
        } else if (result.secondaryTitle) {
            title.push(result.secondaryTitle);
        }
        const ariaTitle = [
            result.groupingHeader,
            title.join(' '),
            result.info1,
            result.info2,
        ]
            .filter((v) => v != null)
            .join(', ');
        return el(
            'li.frontpage-search__result__item',
            { 'aria-label': ariaTitle },
            el(
                'a.frontpage-search__result__link',
                {
                    href: result.url,
                    'data-automation-id': 'global_search_suggestion',
                    'data-group-index': index,
                    'data-track': JSON.stringify(result.data.track),
                    'aria-label': ariaTitle,
                },
                el(
                    'div.frontpage-search__result__text',
                    null,
                    renderTitle(
                        result.title,
                        result.titleSupplement,
                        result.secondaryTitle,
                    ),
                    result.info1 ? renderInfoSingleLine(result.info1) : null,
                    ' ',
                    result.info2 ? renderInfo2(result.info2) : null,
                ),
            ),
        );
    }

    function renderResultNettbil(result, index) {
        return el(
            'li.frontpage-search__result__item',
            null,
            el(
                'a.frontpage-search__result__link',
                {
                    href: result.url,
                    'data-automation-id': 'global_search_suggestion',
                    'data-group-index': index,
                    'data-track': JSON.stringify(result.data.track),
                },
                el(
                    'div.frontpage-search__result__group-title',
                    {},
                    el('img.frontpage-search__result__image', {
                        src: result.imgUrl,
                        width: '100',
                        height: '75',
                    }),
                ),
                el(
                    'div.frontpage-search__result__text',
                    null,
                    renderTitle(
                        result.title,
                        result.titleSupplement,
                        result.secondaryTitle,
                    ),
                    result.info1 ? renderInfoSingleLine(result.info1) : null,
                    ' ',
                    result.info2 ? renderInfo2(result.info2) : null,
                ),
            ),
        );
    }

    function renderSiteGroup(group) {
        if (group.description === 'Nettbil') {
            return el(
                'div.frontpage-search__result',
                { 'data-group-type': group.type },
                el(
                    'ol.frontpage-search__result__items',
                    { role: 'presentation' },
                    group.results.map(renderResultNettbil),
                ),
            );
        }
        return el(
            'div.frontpage-search__result',
            { 'data-group-type': group.type },
            el(
                'h2.frontpage-search__result__group-title',
                null,
                group.description,
            ),
            // role=presentation here is because of a VoiceOver bug which doesn't read items unless we do this
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation' },
                group.results.map(renderResult),
            ),
        );
    }

    function renderGroup(group) {
        return el(
            'div.frontpage-search__result',
            { 'data-group-type': group.type },
            el(
                'h2.frontpage-search__result__group-title',
                null,
                group.description,
            ),
            // role=presentation here is because of a VoiceOver bug which doesn't read items unless we do this
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation' },
                group.results.map(renderResult),
            ),
        );
    }

    function renderResultCommand(result) {
        return el(
            'li.frontpage-search__result__item',
            null,
            el(
                'div.frontpage-search__result__text',
                null,
                renderTitle(result.title, result.titleSupplement),
                renderInfoSingleLine(result.info1),
            ),
        );
    }

    function renderGroupCommand(group) {
        return el('div.frontpage-search__result', null, [
            el(
                'h2.frontpage-search__result__group-title',
                null,
                group.description,
            ),
            el(
                'ol.frontpage-search__result__items',
                { role: 'presentation' },
                group.results.map(renderResultCommand),
            ),
        ]);
    }

    function renderGroupData(group) {
        if (where === 'BROWSE') {
            switch (group.type) {
                case 'COMPANYPROFILE':
                    return renderGroupCompanyProfile(group);

                case 'SEARCH_FINN':
                    return renderGroupSearchFinn(group);

                case 'AD':
                    return renderGroupAd(group);

                default:
                    return renderGroup(group);
            }
        }

        switch (group.type) {
            case 'AD':
                return renderGroupAd(group);

            case 'SITE':
                return renderSiteGroup(group);

            case 'COMMAND':
                return renderGroupCommand(group);

            case 'SEARCH_FINN':
                return renderGroupSearchFinn(group);

            default:
                return renderGroup(group);
        }
    }

    function setStatus(text) {
        if (suggestionsDropdown.setStatus) {
            suggestionsDropdown.setStatus(text);
        }
    }

    function render(groups, onsuccess) {
        dropdownEle = el('div', null, groups.map(renderGroupData));
        dropdownEle.addEventListener('touchmove', () => {
            inputEle.blur();
        });
        onsuccess(dropdownEle);
        if (items.length !== 0) {
            setStatus(
                `${groups
                    .map((group) => group.results.length)
                    .reduce((a, b) => a + b, 0)} forslag`,
            );
        }
    }

    function trackAndRelocate(element, a) {
        trackPickedItem(element, a);
        trackViewedItems(element);
        const url = new URL(a.href);
        if (bapOpenFiltersOverlayEnabled) {
            url.searchParams.set('filters', '');
        }
        window.location = url.href;
    }

    function getRenderOptions() {
        return {
            updateValue: false,
            hideOnDeactivation: isDropdown,
            selectedClass: 'frontpage-search__result__selected',
            dropdownClass:
                where === 'LANDINGPAGE' ? '' : 'frontpage-search__container',
            statusClass: 'frontpage-search__status',
            insertionElement: form.querySelector('[data-dropdown-insertion]'),
            timeout: 0,
            onsubmit(item, ele) {
                if (!item || item.length < 1) {
                    return;
                }

                if (!dropdownEle) {
                    form.submit();
                    return;
                }

                const chooseMeEle = dropdownEle.querySelector(
                    '[data-choose-me]',
                );
                if (!ele && chooseMeEle) {
                    trackSubmit();
                    const rawQuery = new URL(chooseMeEle.href).searchParams.get(
                        'q',
                    );
                    const encodedRawQuery = encodeURI(rawQuery);
                    const encodedCompleteQuery = encodeURI(item);
                    const url = new URL(
                        chooseMeEle.href.replace(
                            encodedRawQuery,
                            encodedCompleteQuery,
                        ),
                    );

                    if (bapOpenFiltersOverlayEnabled) {
                        url.searchParams.set('filters', '');
                    }
                    window.location = url.href;
                    /* eslint-disable consistent-return */
                    return true;
                }

                const a = ele && ele.querySelector('a');
                if (a && a.href) {
                    trackAndRelocate(dropdownEle, a);
                    return true;
                }

                if (!ele && dropdownEle && dropdownEle.childElementCount > 0) {
                    const allEle = Array.from(dropdownEle.children).filter(
                        (child) =>
                            child.getAttribute('data-group-type') !==
                            'SEARCH_FINN',
                    );
                    if (allEle.length === 1) {
                        const firstEleResults = allEle[0].querySelectorAll(
                            'li',
                        );
                        if (firstEleResults && firstEleResults.length === 1) {
                            const onlyEle = firstEleResults[0];
                            const onlyA = onlyEle && onlyEle.querySelector('a');
                            if (onlyA && onlyA.href) {
                                trackAndRelocate(dropdownEle, onlyA);
                                return true;
                            }
                        }
                    }
                }
                /* eslint-enable consistent-return */

                trackSubmit();
                form.submit();
            },
            onclose() {
                if (render.cancel) {
                    render.cancel();
                }
            },
            contentGetter(ele) {
                return (ele.querySelector('[data-title]') || ele).textContent;
            },
        };
    }

    function trackInput(event) {
        tracking.trackEvent({
            analyticsName: 'search.search-for-something.typed',
            eventFlags: 'start',
            stringValue: getTrackingLocation(where, searchKeys),
        });
        event.target.removeEventListener('input', trackInput);
    }

    function hideSpinner() {
        if (spinnerEle) {
            spinnerEle.remove();
        }
    }

    function reset() {
        req = null;
        if (items.length > 0) {
            setStatus(`${items.length} forslag`);
            items.length = 0;
        }
        hideSpinner();
    }

    function streamAbort() {
        if (req) {
            req.close();
            reset();
        }
    }

    function createFetchUrl(base, term) {
        const url = new URL(base);

        url.searchParams.set('dropdown', !isGlobal);
        url.searchParams.set('searchKeys', searchKeys);
        url.searchParams.set('client', client);
        url.searchParams.set('beta', beta);
        url.searchParams.set('where', where);
        url.searchParams.set('term', term);

        return url.href;
    }

    function abort() {
        if (canceler) {
            canceler();
            reset();
            canceler = null;
        }
    }

    function handleEmptyResult(term) {
        suggestionsDropdown.hide();
        if (isGlobal && term) {
            form.querySelector('[data-dropdown-insertion]').append(noHitsEle);
        }
    }

    function groupItems(itemsInput) {
        const groups = [];
        itemsInput.forEach((item) => {
            let gr = groups.filter(
                (g) => g.groupingHeader === item.groupingHeader,
            )[0];
            if (!gr) {
                gr = {
                    type: item.resultType,
                    groupingHeader: item.groupingHeader,
                    description: item.groupingHeader,
                    groupOrder: item.groupOrder,
                    itemOrder: item.itemOrder,
                    results: [],
                };
                groups.push(gr);
            }

            const linkExists = gr.results.some(
                (result) =>
                    result.url &&
                    item.url &&
                    result.url.toLowerCase() === item.url.toLowerCase(),
            );

            // We only display one item with the same title in a group
            if (!linkExists) {
                gr.results.push(item);
            }
        });
        return groups;
    }

    function sortResultsInGroups(groups) {
        groups.forEach((group) => {
            group.results.sort((a, b) => b.itemOrder - a.itemOrder);
        });
    }

    function sortGroups(groups) {
        groups.sort((a, b) => b.groupOrder - a.groupOrder);
    }

    function fetch(term, onsuccess) {
        lastTerm = term;

        abort();

        const xhr = new XMLHttpRequest();

        xhr.open('GET', createFetchUrl(suggestUrl, term));
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader(
            'Content-Type',
            'application/x-www-form-urlencoded',
        );
        xhr.withCredentials = true;
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.responseText) {
                const responseItems = JSON.parse(xhr.responseText);

                if (responseItems.length === 0) {
                    handleEmptyResult();
                } else {
                    if (isGlobal) {
                        noHitsEle.remove();
                    }
                    const groups = groupItems(responseItems);
                    sortResultsInGroups(groups);
                    sortGroups(groups);
                    render(groups, onsuccess);
                }
            }
        };

        xhr.send();

        canceler = () => xhr.abort();
    }

    function streamFetch(term, onsuccess) {
        lastTerm = term;

        streamAbort();

        const url = createFetchUrl(streamingSuggestUrl, term);

        try {
            req = new EventSource(url, { withCredentials: true });
        } catch (e) {
            // e.g. no CORS
            suggestionsDropdown.fetch = fetch;
            suggestionsDropdown.abort = abort;
            fetch(term, onsuccess);
            return;
        }

        req.addEventListener('message', (e) => {
            if (e.data === '') {
                if (items.length === 0) {
                    handleEmptyResult();
                }
                streamAbort();
                hideSpinner();
                return;
            }

            try {
                items.push(JSON.parse(e.data));
            } catch (ex) {
                return;
            }

            if (isGlobal) {
                noHitsEle.remove();
            }
            const groups = groupItems(items);
            sortResultsInGroups(groups);
            sortGroups(groups);
            render(groups, onsuccess);
        });

        req.addEventListener('', handleEmptyResult(term));
    }

    /* eslint-disable */
    // https://remysharp.com/2010/07/21/throttling-function-calls
    function throttle(fn, threshhold, scope) {
        threshhold || (threshhold = 250);
        var last, deferTimer;

        var throttledFunction = function () {
            var context = scope || this;

            var now = +new Date(),
                args = arguments;
            if (last && now < last + threshhold) {
                // hold on to it
                clearTimeout(deferTimer);
                deferTimer = setTimeout(function () {
                    last = now;
                    fn.apply(context, args);
                }, threshhold);
            } else {
                last = now;
                deferTimer = setTimeout(function () {
                    fn.apply(context, args);
                }, threshhold);
            }
        };

        throttledFunction.cancel = function () {
            clearTimeout(deferTimer);
        };

        return throttledFunction;
    }
    /* eslint-enable */

    const options = getRenderOptions();
    inputEle.addEventListener('input', trackInput);

    if ('EventSource' in window) {
        suggestionsDropdown = new SuggestionsDropdown(
            inputEle,
            streamFetch,
            streamAbort,
            options,
        );

        // Buffer the streaming results to avoid re-rendering too often
        /* eslint-disable no-func-assign */
        render = throttle(render, 50);
        /* eslint-enable no-func-assign */
    } else {
        suggestionsDropdown = new SuggestionsDropdown(
            inputEle,
            fetch,
            abort,
            options,
        );
    }

    if (isGlobal && inputEle.value.length > 1) {
        inputEle.selectionStart = inputEle.value.length;
        suggestionsDropdown.fetch(inputEle.value, suggestionsDropdown.show);
    }
};
